import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
  SavingsBalanceChart as SavingsBalanceChartComponent,
  Slider,
  Link,
} from '@trp-rps-rps-shared-web/rps-shared-web-components';
import * as tokens from '@trp-rps-rps-shared-web/rps-shared-web-styles';
import styled from 'styled-components';
import ChartHeader from './ChartHeader';
import { formatAmountWithoutDecimals, keyDown } from '../../utils/helpers';
import { chartsActions, modalActions } from '../../redux/root-action';
import { modals, chartTypes, charts } from '../../utils/constants';
import { getIncomeGoal } from '../../selectors';

const StyledChartContent = styled.div`
  padding: 0px 64px 0px 64px;
  @media (max-width: 1024px) {
    padding: 0px 48px 0px 48px;
  }
  @media (max-width: 768px) {
    padding: 0px;
  }
  > div:nth-of-type(2) {
    @media(min-width: 1025px) {
      display: block;
    }
  }
`;

const StyledSliderContainer = styled.div`
  padding: 26px 3px 0px 50px;
  margin: -16px 0 0 0;
  > div {
    margin: 0;
  }
  @media (max-width: 1024px) {
    margin: -10px -2px 0 -4px;
  }
`;

const ShowDetailsContainer = styled.div`
  margin: 24px 78px 8px 122px;
  @media only screen and (max-device-width: 1024px) {
    display: block;
    margin: 24px 8px 8px;
  }
  @media only screen and (max-device-width: 1024px) {
    display: block;
  }
  @media only screen and (width: 1024px) and (orientation: landscape) {
    margin: 24px 48px 48px;
  }
`;

const ShowDetailsHeader = styled.div`
  font-family: Nimbus Sans Regular;
  font-size: 16px;
  line-height: 22px;
  text-align: left;
  margin-bottom: 8px;
  color: ${tokens.ColorBrandInteractiveBlue};
`;

const CollapseIcon = styled.img`
  margin-left: 8px;
  width: 8px;
  height: 8px;
  cursor: pointer;
`;

const GearIcon = styled.img`
  position: relative;
  top: -2px;
  margin-right: 8px;
`;
const ShowDetailsRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid ${tokens.ColorWhite200};
  @media only screen and (min-device-width: 1024px) {
    border-bottom: none;
    padding: 4px 0;
  }
`;

const ShowDetailsLegend = styled.div`
  width: 24px;
`;

const ShowDetailsLegendDots = styled.div`
  height: 4px;
  width: 24px;
  overflow: hidden;
  border-bottom: 4px dotted #006894;
`;

const ShowDetailsLegendBlue = styled.div`
  width: 16px;
  height: 4px;
  border-radius: 2px;
  margin-left: 4px;
  background-color: ${tokens.ColorBrandBrightBlue};
`;

const ShowDetailsLabel = styled.div`
  font-family: Nimbus Sans Regular;
  font-size: 16px;
  line-height: 22px;
  text-align: left;
  color: ${tokens.ColorGrey900};
  padding-left: 8px;
  padding-right: 8px;
  @media only screen and (min-device-width: 1024px) {
    padding-left: 0;
  }
`;

const ShowDetailsValue = styled.div`
  flex: 30%;
  font-family: Nimbus Sans Regular;
  font-size: 16px;
  line-height: 22px;
  text-align: right;
  color: ${tokens.ColorGrey900};
`;

const ShowDetailsLink = styled.div`
  margin-bottom: 24px;
`;

const ShowDetailsFootnote = styled.div`
  font-family: Nimbus Sans Regular;
  font-size: 12px;
  line-height: 18px;
  color: ${tokens.ColorGrey600};
  padding: 24px 0 8px;
`;

const BoldText = styled.span`
  font-family: Nimbus Sans Bold;
`;

const ExpensesContainer = styled.div`
  display: flex;
  flex-direction: row;  
  width: 100%;
  div {
    flex: 1;
    font-size: 16px;
    line-height: 22px;
    
    color:  ${tokens.ColorGrey700};
    :first-of-type {
      padding-left: 36px;
    }
    :last-of-type {
      text-align: right;
    }
    @media only screen and (min-width: 1024px) {
      padding-top: 8px;
    }
    @media only screen and (max-width: 1023px) {
      :first-of-type {
        padding-left: 32px;
      }
    }
  }
  @media only screen and (max-width: 1023px) {
    margin-bottom: 8px;
  }
`;

class SavingsBalanceChart extends Component<any> {
  props: any;
  state: any;
  constructor(props: any) {
    super(props);
    this.state = {
      showDetails: true,
      showExpenses: true,
    };
  }

  getSimulationData = () => {
    const { selectedChartType, selectedAge, simulationCurrentWant, simulationCurrentHave } = this.props;
    const simulationData = selectedChartType === chartTypes.WANT ? simulationCurrentWant : simulationCurrentHave;
    const {
      participantAge,
      simulationType,
      runoutAge,
      desiredRetirementAge,
      householdExpenseEventInFutureDollars,
      householdIncomeEventInFutureDollars,
      advisedBalanceInFutureDollars,
      projectedBalanceInFutureDollars,
    } = simulationData;
    const series = [];

    if (projectedBalanceInFutureDollars && projectedBalanceInFutureDollars.length) {
      series.push({
        type: 'line',
        name: 'Projected Savings',
        data: projectedBalanceInFutureDollars.map((amount: number) => Math.trunc(amount)),
        color: tokens.ColorChartingBrightBlue300,
      });
    }

    if (projectedBalanceInFutureDollars && advisedBalanceInFutureDollars && advisedBalanceInFutureDollars.length) {
      for (let i = 0; i < projectedBalanceInFutureDollars.length; i++) {
        if (projectedBalanceInFutureDollars[i] === advisedBalanceInFutureDollars[i]) {
          continue;
        } else {
          series.push({
            type: 'line',
            name: 'Target Savings',
            data: advisedBalanceInFutureDollars.map((amount: number) => Math.trunc(amount)),
            color: tokens.ColorBrandInteractiveBlue,
          });
          break;
        }
      }
    }
    return {
      participantAge,
      runOutAge: runoutAge === 0 || participantAge.length === 0 ? null : runoutAge + participantAge[0],
      simulationType: simulationType,
      retirementAge: desiredRetirementAge,
      hoveredAge: selectedAge,
      series,
      householdExpenseEventInFutureDollars,
      householdIncomeEventInFutureDollars,
    };
  };

  expandShowDetails = () => {
    const { showDetails } = this.state;
    this.setState({
      showDetails: showDetails ? false : true,
    });
  };

  toggleExpenses = () => {
    const { showExpenses } = this.state;
    this.setState({
      showExpenses: showExpenses ? false : true,
    });
  };

  showEditSettingsModal = () => {
    const { showModal } = this.props;
    showModal(modals.EDIT_CHART_DISPLAY_SETTINGS);
  };

  getProjectedSavingsAtAge = (data: any, age: number) => {
    const projectedSavingsObj = data.series.find((s: any) => s.name === 'Projected Savings');
    if (projectedSavingsObj && projectedSavingsObj.name) {
      return projectedSavingsObj.data[age ? age - data.participantAge[0] : 0];
    }
    return 0;
  };

  getTargetSavingsAtAge = (data: any, age: number) => {
    const targetSavingsObj = data.series.find((s: any) => s.name === 'Target Savings');
    if (targetSavingsObj && targetSavingsObj.name) {
      return targetSavingsObj.data[age ? age - data.participantAge[0] : 0];
    }
    return 0;
  };

  getIncomeGapAtAge = (data: any, age: number) => {
    return this.getProjectedSavingsAtAge(data, age) - this.getTargetSavingsAtAge(data, age);
  };

  render() {
    const {
      selectedChartType,
      simulationCurrentWant,
      simulationCurrentWant: { participantAge },
      simulationCurrentHave,
      selectAge,
      selectedAge,
      incomeGoal,
      birthDate,
    } = this.props;
    const { showDetails, showExpenses } = this.state;
    let today = new Date();
    let dob = new Date(birthDate);
    let currentAge = today.getFullYear() - dob.getFullYear();
    let ageValue = 0;
    if (
      (selectedChartType === chartTypes.WANT && !simulationCurrentWant.simulationType) ||
      (selectedChartType === chartTypes.HAVE && !simulationCurrentHave.simulationType)
    ) {
      return null;
    }
    const simulationData = this.getSimulationData() && this.getSimulationData();

    const {      
      householdExpenseEventInFutureDollars,
      householdIncomeEventInFutureDollars
    } = simulationData;
    const mobile = window.matchMedia('(max-width: 1023px)');
    return (
      <React.Fragment>
        <ChartHeader
          id={charts.SAVINGS_BALANCE}
          onTrack={this.getSimulationData() && this.getSimulationData().runOutAge ? false : true}
          spendAmount={formatAmountWithoutDecimals(Math.trunc(incomeGoal))}
          offTrackAmount={formatAmountWithoutDecimals(Math.trunc(incomeGoal))}
          runOutAge={this.getSimulationData().runOutAge ? this.getSimulationData().runOutAge : 0}
        />
        <StyledChartContent>
          <SavingsBalanceChartComponent data={this.getSimulationData() && this.getSimulationData()} />
          <StyledSliderContainer>
            <Slider
            value={selectedAge ? selectedAge : participantAge[0]}
            min={participantAge[0]}
            max={participantAge[participantAge.length - 1]}
            onChange={(age: number) => selectAge(age)}
            ariaLabel="Age Slider Control"
          />
          </StyledSliderContainer>
        </StyledChartContent>
        <ShowDetailsContainer>
          {mobile.matches && (
            <>
              <ShowDetailsRow style={{ border: 'none', marginBottom: '0' }}>
                <ShowDetailsLabel style={{ paddingLeft: '0' }}>
                  Savings Balance at <BoldText>Age {selectedAge ? selectedAge : participantAge[0]}</BoldText>
                </ShowDetailsLabel>
                <ShowDetailsValue>
                  <BoldText>
                    {formatAmountWithoutDecimals(this.getProjectedSavingsAtAge(simulationData, selectedAge))}
                  </BoldText>
                </ShowDetailsValue>
              </ShowDetailsRow>
              
              <ShowDetailsHeader
                onClick={this.expandShowDetails}
                tabIndex={0}
                onKeyPress={(e: React.KeyboardEvent<HTMLDivElement>) => keyDown(e, this.expandShowDetails)}
              >
                {showDetails ? 'Hide ' : 'Show '} Details
                <CollapseIcon
                  src={
                    showDetails
                      ? `data:image/svg+xml;base64,${tokens.AssetIconDropdownUpArrowBlue}`
                      : `data:image/svg+xml;base64,${tokens.AssetIconDropdownDownArrowBlue}`
                  }
                />
              </ShowDetailsHeader>
            </>
          )}
          {showDetails && (
            <>
              {simulationData.series.length > 1 && (
                <ShowDetailsRow>
                  <ShowDetailsLegend>
                    <ShowDetailsLegendDots />
                  </ShowDetailsLegend>
                  {mobile.matches ? (
                    <ShowDetailsLabel>Target Savings</ShowDetailsLabel>
                    ) : (
                      <ShowDetailsLabel><BoldText>Target Savings at Age {selectedAge ? selectedAge : participantAge[0]}</BoldText></ShowDetailsLabel>
                    )}
                  
                  <ShowDetailsValue>
                    <BoldText>{formatAmountWithoutDecimals(this.getTargetSavingsAtAge(simulationData, selectedAge))}</BoldText>
                  </ShowDetailsValue>
                </ShowDetailsRow>
              )}

              <ShowDetailsRow style={{ borderBottom: 'none'}}>
                <ShowDetailsLegend>
                  <ShowDetailsLegendBlue />
                </ShowDetailsLegend>
                <ShowDetailsLabel onClick={(householdExpenseEventInFutureDollars || householdIncomeEventInFutureDollars) && this.toggleExpenses} style={{cursor: 'pointer', fontFamily: mobile.matches ? 'Nimbus Sans Regular' : 'Nimbus Sans Bold' }}>Projected Savings</ShowDetailsLabel>
                
                <CollapseIcon
                alt="Projected Savings toggle icon"
              onClick={(householdExpenseEventInFutureDollars || householdIncomeEventInFutureDollars) && this.toggleExpenses} 
              src={
                showExpenses
                  ? `data:image/svg+xml;base64,${tokens.AssetIconDropdownUpArrowBlue}`
                  : `data:image/svg+xml;base64,${tokens.AssetIconDropdownDownArrowBlue}`
              }
              />
                <ShowDetailsValue>
                  <BoldText>{formatAmountWithoutDecimals(this.getProjectedSavingsAtAge(simulationData, selectedAge))}</BoldText>
                </ShowDetailsValue>
              </ShowDetailsRow>
              
              {showExpenses && householdIncomeEventInFutureDollars && householdIncomeEventInFutureDollars.map((income: any, index: number) => {      
                ageValue = selectedAge ? selectedAge - currentAge : 0;                      
                return (
                  <ExpensesContainer>                    
                    <div>{income.name}</div>
                    <div>{income.value[ageValue] !== 0 && '+'}{formatAmountWithoutDecimals(Math.trunc(income.value[ageValue]))}</div>
                  </ExpensesContainer>
                );
              })} 
              {showExpenses && householdExpenseEventInFutureDollars && householdExpenseEventInFutureDollars.map((event: any, index: number) => {      
                ageValue = selectedAge ? selectedAge - currentAge : 0;                      
                return (
                  <ExpensesContainer>                    
                    <div>{event.name}</div>
                    <div>{formatAmountWithoutDecimals(Math.trunc(event.value[ageValue]))}</div>
                  </ExpensesContainer>
                );
              })} 
              
              {simulationData.series.length > 1 && (
                <ShowDetailsRow style={{
                    marginTop: mobile.matches ? 'auto' : '32px', 
                    marginBottom: mobile.matches ? '24px' : 'auto', 
                    borderTop: mobile.matches ? '1px solid ' + tokens.ColorWhite200 : '', 
                    }}>
                  <ShowDetailsLabel>
                    <BoldText>Balance Gap</BoldText>
                  </ShowDetailsLabel>
                  <ShowDetailsValue
                    style={{
                      color:
                        this.getIncomeGapAtAge(simulationData, selectedAge) >= 0
                          ? tokens.ColorFunctionalGreen500
                          : tokens.ColorFunctionalRed500,
                    }}
                  >
                    <BoldText>
                      {formatAmountWithoutDecimals(Math.abs(this.getIncomeGapAtAge(simulationData, selectedAge)))}
                    </BoldText>
                  </ShowDetailsValue>
                </ShowDetailsRow>
              )}
              {mobile.matches && (
              <ShowDetailsLink>
                <GearIcon src={`data:image/svg+xml;base64,${tokens.AssetIconGear}`} />
                <Link
                  linkLabel={'Edit Chart Display Settings'}
                  onClick={this.showEditSettingsModal}
                  tabIndex={0}
                  onKeyPress={(e: React.KeyboardEvent<HTMLDivElement>) => keyDown(e, this.showEditSettingsModal)}
                  color={tokens.ColorBrandInteractiveBlue}
                  hoverColor={tokens.ColorBrandInteractiveBlueHover}
                />
              </ShowDetailsLink>
              )}
              <ShowDetailsFootnote>
                *Higher withdrawal amounts may be necessary due to withholding requirements or the need to pay taxes.
                Estimated taxes have been taken out of Social Security and any applicable pension amounts.
              </ShowDetailsFootnote>
            </>
          )}
        </ShowDetailsContainer>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state: any) => {
  const {
    charts: { selectedChart, selectedAge, selectedChartType, simulationCurrentWant, simulationCurrentHave },
    riScenario: {
      participantProfile,
    },
  } = state;
  return {
    selectedChart,
    selectedChartType,
    simulationCurrentWant,
    simulationCurrentHave,
    selectedAge,
    incomeGoal: getIncomeGoal(state),
    birthDate: participantProfile && participantProfile.birthDate
  };
};

export default connect(mapStateToProps, {
  selectAge: chartsActions.selectAge,
  showModal: modalActions.showModal,
})(SavingsBalanceChart);
